import React, { useEffect, useState, type FC } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
// eslint-disable-next-line @atlaskit/design-system/use-spotlight-package
import { SpotlightTarget, SpotlightTransition } from '@atlaskit/onboarding';
import { PopoverContent, PopoverProvider, PopoverTarget } from '@atlaskit/spotlight';

import { useFeatureDiscovery } from '@confluence/feature-discovery';
import { fg } from '@confluence/feature-gating';
import { LoadableAfterPaint } from '@confluence/loadable';

import { CurrentSelectionSpan } from './CurrentSelectionSpan.tsx';
import type { SelectionRect } from './CurrentSelectionSpan.tsx';
import { READING_AIDS_ACRONYM_SPOTLIGHT_MESSAGE_ID } from './ReadingAidsAcronymSpotlight.tsx';

const ReadingAidsAcronymSpotlight = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ReadingAidsAcronymSpotlight" */
				'./ReadingAidsAcronymSpotlight.tsx'
			)
		).ReadingAidsAcronymSpotlight,
});

type ReadingAidsSpotlightWrapperProps = {
	contentId: string;
	selectionRect: SelectionRect;
	showSpotlight: boolean;
};

/**
 * Wrapper component for the Reading Aids spotlight/onboarding UI.
 * This component is lazy-loaded so that:
 * 1. `useFeatureDiscovery` hook is only called when the wrapper is mounted
 * 2. `@atlaskit/onboarding` (SpotlightTarget, SpotlightTransition) and
 *    `@atlaskit/spotlight` (PopoverContent, PopoverProvider, PopoverTarget)
 *    are only loaded when the spotlight is needed
 */
export const ReadingAidsSpotlightWrapper: FC<ReadingAidsSpotlightWrapperProps> = ({
	contentId,
	selectionRect,
	showSpotlight,
}) => {
	const [spotlightEnabled, stopSpotlight] = useFeatureDiscovery(
		READING_AIDS_ACRONYM_SPOTLIGHT_MESSAGE_ID,
	);
	const [spotlightActive, setSpotlightActive] = useState<boolean>(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const endSpotlight = () => {
		void stopSpotlight();
		setSpotlightActive(false);
	};

	useEffect(() => {
		if (spotlightEnabled && showSpotlight) {
			setSpotlightActive(true);

			createAnalyticsEvent({
				type: 'sendScreenEvent',
				data: {
					source: 'viewPageScreen',
					name: 'readingAidsAcronymSpotlight',
					attributes: {
						contentId,
					},
				},
			}).fire();
		}
	}, [contentId, createAnalyticsEvent, showSpotlight, spotlightEnabled]);

	if (fg('confluence_frontend_spotlight_ads_migration')) {
		return (
			<PopoverProvider>
				<PopoverTarget>
					<CurrentSelectionSpan
						id={READING_AIDS_ACRONYM_SPOTLIGHT_MESSAGE_ID}
						top={selectionRect.top}
						left={selectionRect.left}
						width={selectionRect.width}
						height={selectionRect.height}
					/>
				</PopoverTarget>
				<PopoverContent
					isVisible={spotlightActive}
					placement="bottom-start"
					strategy="absolute"
					dismiss={endSpotlight}
				>
					{spotlightActive && (
						<ReadingAidsAcronymSpotlight contentId={contentId} onDismissClick={endSpotlight} />
					)}
				</PopoverContent>
			</PopoverProvider>
		);
	}

	return (
		<>
			<SpotlightTarget name={READING_AIDS_ACRONYM_SPOTLIGHT_MESSAGE_ID}>
				<CurrentSelectionSpan
					id={READING_AIDS_ACRONYM_SPOTLIGHT_MESSAGE_ID}
					top={selectionRect.top}
					left={selectionRect.left}
					width={selectionRect.width}
					height={selectionRect.height}
				/>
			</SpotlightTarget>
			<SpotlightTransition>
				{spotlightActive && (
					<ReadingAidsAcronymSpotlight contentId={contentId} onDismissClick={endSpotlight} />
				)}
			</SpotlightTransition>
		</>
	);
};
